// src/pages/buyer/AddressesPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, MapPin } from 'lucide-react'
import { addressApi } from '../../api/services'
import { Card, CardBody, Button, PageSpinner, Empty } from '../../components/common/ui'
import toast from 'react-hot-toast'

const INIT = { label:'Home', full_name:'', mobile:'', address_line1:'', address_line2:'', landmark:'', city:'', state:'', pincode:'', type:'both', is_default:false }

export default function AddressesPage() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(INIT)
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))

  const { data, isLoading } = useQuery({ queryKey: ['addresses'], queryFn: () => addressApi.list() })
  const save = useMutation({
    mutationFn: () => editing === 'new' ? addressApi.create(form) : addressApi.update(editing, form),
    onSuccess: () => { toast.success('Saved'); qc.invalidateQueries(['addresses']); setEditing(null) },
    onError: e => toast.error(e.response?.data?.message || 'Failed'),
  })
  const del = useMutation({
    mutationFn: id => addressApi.delete(id),
    onSuccess: () => { toast.success('Removed'); qc.invalidateQueries(['addresses']) },
  })
  const setDefault = useMutation({
    mutationFn: id => addressApi.setDefault(id),
    onSuccess: () => qc.invalidateQueries(['addresses']),
  })

  const startEdit = (addr) => { setEditing(addr.id); setForm({ ...INIT, ...addr, is_default: addr.is_default ?? false }) }
  const startNew = () => { setEditing('new'); setForm(INIT) }

  if (isLoading) return <PageSpinner />
  const addresses = data?.data?.data || []

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-display font-bold flex items-center gap-2"><MapPin size={20} className="text-pink" /> Addresses</h1>
        <Button size="sm" onClick={startNew}><Plus size={14} /> Add Address</Button>
      </div>

      {editing && (
        <Card className="mb-6">
          <CardBody className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              {['Home','Work','Other'].map(l => (
                <button key={l} onClick={() => setForm(f => ({ ...f, label: l }))}
                  className={`py-2 rounded-xl border text-sm transition-all ${form.label === l ? 'border-pink bg-pink-light text-pink font-bold' : 'border-border hover:border-pink'}`}>{l}</button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input className="input" placeholder="Full name *" value={form.full_name} onChange={set('full_name')} />
              <input className="input" placeholder="Mobile *" value={form.mobile} onChange={set('mobile')} />
            </div>
            <input className="input w-full" placeholder="Address line 1 *" value={form.address_line1} onChange={set('address_line1')} />
            <input className="input w-full" placeholder="Address line 2" value={form.address_line2} onChange={set('address_line2')} />
            <div className="grid sm:grid-cols-3 gap-3">
              <input className="input" placeholder="City *" value={form.city} onChange={set('city')} />
              <input className="input" placeholder="State *" value={form.state} onChange={set('state')} />
              <input className="input" placeholder="Pincode *" value={form.pincode} onChange={set('pincode')} maxLength={6} />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_default} onChange={set('is_default')} className="rounded" />
              Set as default address
            </label>
            <div className="flex gap-2">
              <Button loading={save.isPending} onClick={() => save.mutate()} disabled={!form.full_name || !form.address_line1 || !form.city || !form.pincode}>Save Address</Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </CardBody>
        </Card>
      )}

      {addresses.length === 0 && !editing && (
        <Empty icon="📍" title="No addresses saved" description="Add a delivery address to get started." action={<Button onClick={startNew}>Add Address</Button>} />
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {addresses.map(addr => (
          <Card key={addr.id} className={addr.is_default ? 'border-pink' : ''}>
            <CardBody>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-surface border border-border">{addr.label}</span>
                    {addr.is_default && <span className="text-xs font-bold text-pink">Default</span>}
                  </div>
                  <p className="font-bold text-sm">{addr.full_name}</p>
                  <p className="text-xs text-gray-600">{addr.address_line1}{addr.address_line2 ? `, ${addr.address_line2}` : ''}</p>
                  <p className="text-xs text-gray-600">{addr.city}, {addr.state} – {addr.pincode}</p>
                  <p className="text-xs text-muted">{addr.mobile}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(addr)} className="p-1.5 rounded-lg hover:bg-surface"><Pencil size={13} className="text-muted" /></button>
                  <button onClick={() => del.mutate(addr.id)} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 size={13} className="text-red-400" /></button>
                </div>
              </div>
              {!addr.is_default && (
                <button onClick={() => setDefault.mutate(addr.id)} className="text-xs text-pink hover:underline mt-2">Set as default</button>
              )}
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  )
}
